import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useLanguage } from "@/context/LanguageContext";

const TTSContext = createContext(null);
export const useTTS = () => useContext(TTSContext);

export function TTSProvider({ children }) {
  const { lang } = useLanguage() || { lang: { code: "en" } };
  const [supported] = useState(() => typeof window !== "undefined" && "speechSynthesis" in window);
  const [status, setStatus] = useState("idle"); // idle | playing | paused
  const [rate, setRate] = useState(1);
  const [activeText, setActiveText] = useState(null); // currently spoken paragraph (for highlight)
  const paragraphs = useRef([]); // readable strings for current page
  const idx = useRef(0);
  const rateRef = useRef(1);

  useEffect(() => { rateRef.current = rate; }, [rate]);

  // pages register readable content
  const setContent = useCallback((paras) => {
    paragraphs.current = (paras || []).filter(Boolean);
  }, []);

  const pickVoice = useCallback(() => {
    const voices = window.speechSynthesis.getVoices();
    const code = lang.code;
    return (
      voices.find((v) => v.lang?.toLowerCase().startsWith(code + "-")) ||
      voices.find((v) => v.lang?.toLowerCase() === code) ||
      voices.find((v) => v.lang?.toLowerCase().startsWith(code)) ||
      null
    );
  }, [lang]);

  const speakFrom = useCallback((startIdx) => {
    if (!supported) return;
    window.speechSynthesis.cancel();
    const paras = paragraphs.current;
    if (!paras.length) return;
    idx.current = Math.max(0, Math.min(startIdx, paras.length - 1));

    const speakNext = () => {
      if (idx.current >= paras.length) { setStatus("idle"); setActiveText(null); return; }
      const u = new SpeechSynthesisUtterance(paras[idx.current]);
      const v = pickVoice();
      if (v) u.voice = v;
      u.lang = v?.lang || lang.code;
      u.rate = rateRef.current;
      u.onstart = () => setActiveText(paras[idx.current]);
      u.onend = () => { idx.current += 1; if (window.speechSynthesis.speaking === false) speakNext(); };
      window.speechSynthesis.speak(u);
    };
    setStatus("playing");
    setActiveText(paras[idx.current]);
    speakNext();
  }, [supported, pickVoice, lang]);

  const play = useCallback(() => {
    if (!supported) return;
    if (status === "paused") { window.speechSynthesis.resume(); setStatus("playing"); return; }
    speakFrom(status === "idle" ? 0 : idx.current);
  }, [supported, status, speakFrom]);

  const pause = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.pause();
    setStatus("paused");
  }, [supported]);

  const stop = useCallback(() => {
    if (!supported) return;
    window.speechSynthesis.cancel();
    idx.current = 0;
    setStatus("idle");
    setActiveText(null);
  }, [supported]);

  const next = useCallback(() => { speakFrom(idx.current + 1); }, [speakFrom]);
  const prev = useCallback(() => { speakFrom(idx.current - 1); }, [speakFrom]);

  // stop when language changes
  useEffect(() => { stop(); }, [lang.code]); // eslint-disable-line

  useEffect(() => () => { if (supported) window.speechSynthesis.cancel(); }, [supported]);

  const value = { supported, status, rate, setRate, setContent, play, pause, stop, next, prev, activeText, hasContent: () => paragraphs.current.length > 0 };
  return <TTSContext.Provider value={value}>{children}</TTSContext.Provider>;
}
